<template>
  <v-container fluid>
    <v-card>
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-col cols="10" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Список файлов общего назначения') }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="2" class="text-right">
          <btn-icon-tip
              v-if="isAdmin"
              icon="add"
              icon-color="secondaryiconbtn"
              :tip="$t('Добавление файла')"
              :disabled="isLoading"
              @click="openAddStaticFileDialog" />
        </v-col>
      </v-toolbar>
      <v-row class="px-2 pt-2">
        <v-col>
          <v-expansion-panels>
            <v-expansion-panel v-test="{ panel: '' }">
              <v-expansion-panel-header>
                <div id="search">
                  <v-icon>
                    search
                  </v-icon>
                  {{ $t('Фильтры') }}
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager @keydown.native.enter="focusOnBtn('searchBtn')">
                <v-row>
                  <v-col
                      class="pl-2"
                      cols="12"
                      md="5"
                      lg="4"
                      xl="3">
                    <validatable-combobox
                        v-model="params.staticFileType"
                        v-test="{ combobox: 'staticFileType' }"
                        clearable
                        :readonly="isLoading"
                        :values-enum="STATIC_FILE_TYPE_ENUM"
                        :label="$t('Тип файла')"
                        @change="focusOnBtn('searchBtn')" />
                  </v-col>
                  <v-col
                      class="pl-2"
                      cols="12"
                      md="5"
                      lg="4"
                      xl="3">
                    <validatable-text-input
                        v-model="params.fileName"
                        v-test="{ input: 'fileName' }"
                        :label="$t('Наименование файла')"
                        :readonly="isLoading"
                        hide-details
                        @keydown.native.enter="focusOnBtn('searchBtn')" />
                  </v-col>
                  <v-col
                      class="pl-2"
                      cols="12"
                      md="5"
                      lg="4"
                      xl="3">
                    <validatable-text-input
                        v-model="params.comment"
                        v-test="{ input: 'comment' }"
                        :label="$t('Комментарий')"
                        :readonly="isLoading"
                        hide-details
                        @keydown.native.enter="focusOnBtn('searchBtn')" />
                  </v-col>
                  <v-col class="text-right" align-self="center">
                    <btn-icon-tip
                        id="search"
                        ref="searchBtn"
                        icon="search"
                        :disabled="isLoading"
                        icon-color="info"
                        :tip="$t('Поиск')"
                        @click="search" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <general-error v-if="!!generalError" />

      <v-row class="px-2">
        <v-col>
          <paginated-data-table
              v-if="getStaticFiles"
              ref="paginatedTable"
              :headers="headers1"
              :items="files"
              :loading="isLoading"
              item-key="fileName"
              :total-count="totalCount"
              :items-per-page="itemsPerPage"
              single-expand
              show-expand
              :request-method="getStaticFiles">
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.icon="{ item }">
              <v-icon>
                {{ item.icon }}
              </v-icon>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.fileName="{ item }">
              <link-with-tip
                  :href="getSystemFileUrl(item.nameWithoutExt)"
                  :tip="$t('Скачать файл')"
                  :text="item.fileName" />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.contentType="{ item }">
              {{ item.contentType }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.creationDate="{ item }">
              {{ item.creationDate
                ? $moment(item.creationDate).format('DD.MM.YYYY HH:mm')
                : null }}
            </template>

            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.staticFileType="{ item }">
              {{ item.staticFileType
                ? $t(STATIC_FILE_TYPE_ENUM[item.staticFileType])
                :null }}
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row class="py-4">
                  <text-view
                      :display="item.userName"
                      :label="$t('Пользователь, загрузивший файл')" />
                  <text-view
                      :display="item.comment"
                      :label="$t('Описание')" />
                </v-row>
                <v-divider />
                <v-row class="text-left py-4">
                  <btn-icon-tip
                      v-if="item.canEdit"
                      icon="edit"
                      icon-color="primaryiconbtn"
                      :tip="$t('Замена файла')"
                      :disabled="isLoading"
                      @click="openEditStaticFileDialog(item)" />
                  <btn-icon-tip
                      v-if="item.canDelete"
                      icon="delete"
                      icon-color="warning"
                      :tip="$t('Удалить')"
                      :disabled="isLoading"
                      @click="openConfirmDialog(item.fileId)" />
                  <btn-icon-tip
                      v-if="item.fileName"
                      icon="content_copy"
                      icon-color="primaryiconbtn"
                      :tip="$t('Копировать ссылку')"
                      @click="copyLink(item.nameWithoutExt)" />
                </v-row>
              </td>
            </template>
          </paginated-data-table>
        </v-col>
      </v-row>
    </v-card>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <add-static-file-dialog
        v-if="addStaticFileDialog.show"
        :dialog-prop="addStaticFileDialog"
        @close="closeDialog('addStaticFileDialog')" />
    <edit-static-file-dialog
        v-if="editStaticFileDialog.show"
        :dialog-prop="editStaticFileDialog"
        @close="closeDialog('editStaticFileDialog')" />
  </v-container>
</template>

<script>

  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableCombobox from '@/components/reused/ui/ValidatableCombobox.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import PaginatedDataTable from '@/components/reused/ui/PaginatedDataTable.vue'
  import AddStaticFileDialog from './AddStaticFileDialog.vue'
  import EditStaticFileDialog from '../StaticPages/EditStaticFileDialog.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import LinkWithTip from '@/components/reused/ui/LinkWithTip.vue'
  import { mapActions, mapState } from 'vuex'
  import { STATIC_PAGES } from '@/constants/store.js'
  import { STATIC_FILE_TYPE_ENUM } from '@/constants/enums'

  export default {
    components: {
      TextView,
      BtnIconTip,
      GeneralError,
      AddStaticFileDialog,
      EditStaticFileDialog,
      ConfirmDialog,
      LinkWithTip,
      PaginatedDataTable,
      ValidatableTextInput,
      ValidatableCombobox
    },
    data () {
      return {
        STATIC_FILE_TYPE_ENUM,
        isLoading: false,
        itemsPerPage: 20,
        params: {
          fileName: '',
          staticFileType: '',
          comment: ''
        },
        addStaticFileDialog: {
          callback: null,
          show: null
        },
        editStaticFileDialog: {
          file: null,
          callback: null,
          show: null
        },
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        },
        fileIcons: {
          html: 'mdi-language-html5',
          js: 'mdi-nodejs',
          json: 'mdi-code-json',
          md: 'mdi-language-markdown',
          pdf: 'mdi-file-pdf-box',
          png: 'mdi-file-image',
          ico: 'mdi-file-image',
          jpg: 'mdi-file-image',
          jpeg: 'mdi-file-image',
          txt: 'mdi-file-document-outline',
          xls: 'mdi-file-excel',
          xlsx: 'mdi-file-excel',
          docx: 'mdi-file-word',
          doc: 'mdi-file-word'
        }
      }
    },
    computed: {
      ...mapState(STATIC_PAGES.NAMESPACE, {
        staticFilesInfo: state => (state[STATIC_PAGES.STATE.STATIC_FILES] || {})
      }),
      files () {
        return this.items.map(f => {
          const extension = f.fileName.substring(f.fileName.indexOf('.') + 1)

          return {
            ...f,
            icon: this.fileIcons[extension],
            canDelete: f.deletable || this.isMainAdmin,
            canEdit: this.isAdmin
          }
        })
      },
      totalCount () {
        return this.staticFilesInfo.totalCount || 0
      },
      items () {
        return this.staticFilesInfo.items || []
      },
      headers1 () {
        return [
          { text: this.$t(''), align: 'start', sortable: true, value: 'icon' },
          { text: this.$t('Наименование'), align: 'start', sortable: true, value: 'fileName' },
          { text: this.$t('Тип файла'), align: 'start', sortable: false, value: 'staticFileType' },
          { text: this.$t('Тип содержимого'), align: 'start', sortable: false, value: 'contentType' },
          { text: this.$t('Дата загрузки'), align: 'start', sortable: false, value: 'creationDate' },
          { text: '', value: 'data-table-expand' }
        ]
      }

    },
    created () {
      this.clearStore()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(STATIC_PAGES.NAMESPACE, {
        getStaticFilesAction: STATIC_PAGES.ACTIONS.GET_STATIC_FILES,
        clearStaticFilesAction: STATIC_PAGES.ACTIONS.CLEAR_STATIC_FILES,
        deleteStaticFileAction: STATIC_PAGES.ACTIONS.DELETE_STATIC_FILE,
        uploadStaticFileAction: STATIC_PAGES.ACTIONS.UPLOAD_STATIC_FILE,
        editStaticFileAction: STATIC_PAGES.ACTIONS.EDIT_STATIC_FILE
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearStaticFilesAction()
          ])
      },
      search: async function () {
        await this.$refs.paginatedTable.resetPaginationAndGetDataFromServer()
      },
      openConfirmDialog: function (id) {
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Файл будет удален. Продолжить?')
        this.confirmDialog.callback = this.deleteStaticFile
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      openAddStaticFileDialog: function () {
        this.addStaticFileDialog.callback = this.$refs.paginatedTable.getDataFromServer
        this.addStaticFileDialog.show = true
      },
      openEditStaticFileDialog: function (file) {
        this.editStaticFileDialog.file = file
        this.editStaticFileDialog.callback = this.$refs.paginatedTable.getDataFromServer
        this.editStaticFileDialog.show = true
      },
      getStaticFiles: async function (params) {
        try {
          this.isLoading = true
          await this.getStaticFilesAction({
            ...params,
            ...this.params
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      deleteStaticFile: async function (fileId) {
        try {
          this.isLoading = true
          await this.deleteStaticFileAction({
            fileId
          })
          await this.$refs.paginatedTable.getDataFromServer()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      copyLink: function (fileName) {
        this.copyToClipboard(this.getSystemFileUrl(fileName))
      },
      copyToClipboard: function (value) {
        if (window.clipboardData && window.clipboardData.setData) {
          // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
          return window.clipboardData.setData('Text', value)
        } else if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
          const textarea = document.createElement('textarea')
          textarea.textContent = value
          textarea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
          document.body.appendChild(textarea)
          textarea.select()
          try {
            return document.execCommand('copy') // Security exception may be thrown by some browsers.
          } catch (ex) {
            console.warn('Copy to clipboard failed.', ex)
            return false
          } finally {
            document.body.removeChild(textarea)
          }
        }
      }
    }
  }
</script>
